import React, { FC, useEffect, useMemo, useState }                  from 'react';
import { v4 as uuid }                                               from 'uuid';
import { Button, Form, Icon }                                       from 'semantic-ui-react';
import { BasicDialog }                                              from '@src/views/components/modals/BasicDialog';
import { BasicDialogContainer }                                     from '@src/views/components/modals/BasicDialogContainer';
import { Align, SemanticUIColor, Size }                             from '@src/types';
import { useCentralCalls }                                          from '@src/rest/CentralCalls';
import { parseArrayPayload, pickString, useConsultantOptions }      from './offeringHoursData';

const HOUR_LIMITS_TABLE = 'ConsultantSowHourLimits';

interface ConsultantHourBudgetsDialogProps {
    sowId: string;
    sowName: string;
    sowBudgetedHours?: number;
    onClose: () => void;
    onSaved?: () => Promise<void> | void;
}

// One editable row in the budget list; `key` is UI-only.
interface HourBudgetRow {
    key: string;
    consultantId: string;
    hours: string;
}

interface SavedHourLimit {
    id: string;
    consultantId: string;
    totalHours: number;
}

const normalizeHourLimits = ( rows: unknown[] ): SavedHourLimit[] =>
    rows
        .map( row => {
            const id = pickString( row, [ 'Id' ] );
            const consultantId = pickString( row, [ 'ConsultantId' ] );
            const totalHours = Number( pickString( row, [ 'TotalHours' ] ) );
            if( !id || !consultantId || !Number.isFinite( totalHours ) ) return null;
            return { id, consultantId, totalHours };
        } )
        .filter( ( row ): row is SavedHourLimit => row != null );

const newBudgetRow = (): HourBudgetRow => ( { key: uuid(), consultantId: '', hours: '' } );

const ConsultantHourBudgetsDialog: FC<ConsultantHourBudgetsDialogProps> = ( { sowId, sowName, sowBudgetedHours, onClose, onSaved } ) => {

    const { RunInsert, RunUpdate, RunDelete, TableSelect } = useCentralCalls();
    const { consultants, isLoading: isLoadingConsultants } = useConsultantOptions( true );

    const [ budgetRows, setBudgetRows ]                 = useState<HourBudgetRow[]>( [] ),
          [ isLoadingBudgets, setIsLoadingBudgets ]     = useState( true ),
          [ isSaving, setIsSaving ]                     = useState( false ),
          [ error, setError ]                           = useState( '' );

    useEffect( () => {
        let cancelled = false;

        const loadBudgets = async () => {
            setIsLoadingBudgets( true );
            try {
                const response = await TableSelect( HOUR_LIMITS_TABLE, { SOWId: sowId } );
                if( cancelled ) return;
                const rows = normalizeHourLimits( parseArrayPayload( response ) ).map( limit => ( {
                    key: limit.id,
                    consultantId: limit.consultantId,
                    hours: `${ limit.totalHours }`,
                } ) );
                setBudgetRows( rows.length > 0 ? rows : [ newBudgetRow() ] );
            } catch( err ){
                console.error( '[ConsultantHourBudgets] failed to load budgets:', err );
                if( !cancelled ) setError( 'Could not load consultant hour budgets.' );
            } finally {
                if( !cancelled ) setIsLoadingBudgets( false );
            }
        };

        loadBudgets();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ sowId ] );

    const consultantOptions = useMemo(
        () => consultants.map( consultant => ( {
            key: consultant.id,
            value: consultant.id,
            text: consultant.name,
        } ) ),
        [ consultants ]
    );

    const updateBudgetRow = ( key: string, changes: Partial<HourBudgetRow> ) => {
        setBudgetRows( prev => prev.map( row => ( row.key === key ? { ...row, ...changes } : row ) ) );
        if( error ) setError( '' );
    };

    const removeBudgetRow = ( key: string ) => {
        setBudgetRows( prev => prev.filter( row => row.key !== key ) );
        if( error ) setError( '' );
    };

    const budgetedHoursTotal = Math.round( budgetRows.reduce( ( sum, row ) => {
        const parsed = Number( row.hours );
        return row.hours.trim() !== '' && Number.isFinite( parsed ) ? sum + parsed : sum;
    }, 0 ) * 100 ) / 100;
    const hasSowBudget = sowBudgetedHours != null && Number.isFinite( sowBudgetedHours );
    // Positive = hours still to hand out; negative = consultants are budgeted past the SOW total.
    const unallocatedHours = hasSowBudget ? Math.round( ( sowBudgetedHours! - budgetedHoursTotal ) * 100 ) / 100 : null;

    // Returns the desired budgets keyed by lower-cased consultant id, or an error message.
    const parseBudgetRows = (): Map<string, { consultantId: string; totalHours: number }> | string => {
        const desired = new Map<string, { consultantId: string; totalHours: number }>();
        for( const row of budgetRows ){
            const consultantId = row.consultantId.trim();
            const hoursText = row.hours.trim();
            if( !consultantId && !hoursText ) continue;
            if( !consultantId ) return 'Select a consultant for each hour budget row.';
            const totalHours = Number( hoursText );
            if( hoursText === '' || !Number.isFinite( totalHours ) || totalHours < 0 ){
                return 'Budgeted hours must be a number of 0 or more.';
            }
            if( Math.round( totalHours * 100 ) !== totalHours * 100 ){
                return 'Budgeted hours can have at most 2 decimal places.';
            }
            const key = consultantId.toLowerCase();
            if( desired.has( key ) ) return 'Each consultant can only have one hour budget per SOW.';
            desired.set( key, { consultantId, totalHours } );
        }
        return desired;
    };

    // Diffs against what is currently saved (re-read, not cached) so retries and concurrent edits stay consistent.
    const syncHourBudgets = async ( desired: Map<string, { consultantId: string; totalHours: number }> ) => {
        const saved = normalizeHourLimits( parseArrayPayload( await TableSelect( HOUR_LIMITS_TABLE, { SOWId: sowId } ) ) );
        const savedByConsultant = new Map( saved.map( limit => [ limit.consultantId.toLowerCase(), limit ] ) );

        for( const limit of saved ){
            if( !desired.has( limit.consultantId.toLowerCase() ) ){
                await RunDelete( HOUR_LIMITS_TABLE, limit.id );
            }
        }

        for( const [ key, budget ] of desired ){
            const existing = savedByConsultant.get( key );
            if( !existing ){
                await RunInsert( HOUR_LIMITS_TABLE, {
                    Id: uuid(),
                    SOWId: sowId,
                    ConsultantId: budget.consultantId,
                    TotalHours: budget.totalHours,
                } );
            } else if( existing.totalHours !== budget.totalHours ){
                await RunUpdate( HOUR_LIMITS_TABLE, existing.id, { TotalHours: budget.totalHours } );
            }
        }
    };

    const handleSubmit = async () => {
        if( isSaving || isLoadingBudgets ) return;

        const desired = parseBudgetRows();
        if( typeof desired === 'string' ){
            setError( desired );
            return;
        }

        setIsSaving( true );
        setError( '' );
        try {
            await syncHourBudgets( desired );
            await onSaved?.();
            onClose();
        } catch( err ){
            console.error( '[ConsultantHourBudgets] failed to save budgets:', err );
            setError( 'Could not save consultant hour budgets. Some changes may have saved — click Save to retry.' );
        } finally {
            setIsSaving( false );
        }
    };

    return (
        <BasicDialog
            showCancelButton
            showSubmitButton
            submitLabel={ isSaving ? 'Saving...' : 'Save budgets' }
            submitColor={ SemanticUIColor.PURPLE }
            onCancel={ onClose }
            onSubmit={ handleSubmit }
            controlAlign={ Align.CENTER }
            className={ 'no-padding create-client-dialog' }
        >
            <BasicDialogContainer
                title={ `Consultant Hours — ${ sowName }` }
                titleColor={ SemanticUIColor.PURPLE }
                contentPadding={ [ Size.MD, Size.MD, Size.LG, Size.MD ] }
                contentMinHeight={ '24dvh' }
                onClose={ onClose }
            >
                <Form className={ 'create-client-form' }>
                    { error && (
                        <p className={ 'create-client-error' }>
                            <Icon name={ 'warning circle' }/> { error }
                        </p>
                    ) }

                    <div className={ 'create-client-section' }>
                        <div className={ 'create-client-section-header' }>
                            <span>Hour budgets</span>
                        </div>
                        <p className={ 'sow-hour-budget-help' }>
                            Split the SOW's budgeted hours across consultants. Hours they log on this SOW are deducted from their budget.
                        </p>

                        <div className={ 'sow-hour-budget-summary' }>
                            <div className={ 'sow-hour-budget-stat' }>
                                <span className={ 'sow-hour-budget-stat-label' }>SOW budget</span>
                                <span className={ 'sow-hour-budget-stat-value' }>
                                    { hasSowBudget ? `${ sowBudgetedHours } hrs` : 'Not set' }
                                </span>
                            </div>
                            <div className={ 'sow-hour-budget-stat' }>
                                <span className={ 'sow-hour-budget-stat-label' }>Allocated</span>
                                <span className={ 'sow-hour-budget-stat-value' }>{ budgetedHoursTotal } hrs</span>
                            </div>
                            { unallocatedHours != null && (
                                <div className={ `sow-hour-budget-stat${ unallocatedHours < 0 ? ' is-over' : unallocatedHours === 0 ? ' is-balanced' : '' }` }>
                                    <span className={ 'sow-hour-budget-stat-label' }>
                                        { unallocatedHours < 0 ? 'Over SOW budget' : 'Unallocated' }
                                    </span>
                                    <span className={ 'sow-hour-budget-stat-value' }>{ Math.abs( unallocatedHours ) } hrs</span>
                                </div>
                            ) }
                        </div>

                        { isLoadingBudgets ? (
                            <p className={ 'sow-hour-budget-help' }>Loading budgets...</p>
                        ) : budgetRows.map( row => {
                            const takenIds = new Set(
                                budgetRows
                                    .filter( other => other.key !== row.key && other.consultantId )
                                    .map( other => other.consultantId.toLowerCase() )
                            );
                            const rowOptions = consultantOptions.filter(
                                option => !takenIds.has( option.value.toLowerCase() )
                            );
                            return (
                                <div key={ row.key } className={ 'sow-hour-budget-row' }>
                                    <Form.Select
                                        search
                                        options={ rowOptions }
                                        value={ row.consultantId || undefined }
                                        placeholder={ isLoadingConsultants ? 'Loading consultants...' : 'Select consultant' }
                                        disabled={ isSaving || isLoadingConsultants }
                                        onChange={ ( _event, data ) => updateBudgetRow( row.key, { consultantId: String( data.value ?? '' ) } ) }
                                    />
                                    <Form.Input
                                        type={ 'number' }
                                        min={ 0 }
                                        step={ 0.25 }
                                        inputMode={ 'decimal' }
                                        value={ row.hours }
                                        placeholder={ 'Hours' }
                                        disabled={ isSaving }
                                        onChange={ ( _event, data ) => updateBudgetRow( row.key, { hours: String( data.value ?? '' ) } ) }
                                    />
                                    <Button
                                        type={ 'button' }
                                        icon={ 'trash alternate outline' }
                                        basic
                                        title={ 'Remove budget' }
                                        aria-label={ 'Remove budget' }
                                        disabled={ isSaving }
                                        onClick={ () => removeBudgetRow( row.key ) }
                                    />
                                </div>
                            );
                        } ) }

                        { !isLoadingBudgets && unallocatedHours != null && unallocatedHours !== 0 && (
                            <p className={ 'sow-hour-budget-warning' }>
                                <Icon name={ 'info circle' }/>
                                { unallocatedHours < 0
                                    ? `Consultant budgets total ${ budgetedHoursTotal } hrs, ${ Math.abs( unallocatedHours ) } hrs more than the SOW's ${ sowBudgetedHours } budgeted hrs. You can still save.`
                                    : `${ unallocatedHours } of the SOW's ${ sowBudgetedHours } budgeted hrs aren't assigned to a consultant yet. You can still save.` }
                            </p>
                        ) }
                        { !isLoadingBudgets && !hasSowBudget && (
                            <p className={ 'sow-hour-budget-help' }>
                                <Icon name={ 'info circle' }/>
                                This SOW has no budgeted hours set. Add them from Edit SOW to track allocation.
                            </p>
                        ) }

                        <div>
                            <Button
                                type={ 'button' }
                                className={ 'dash-add-btn' }
                                disabled={ isSaving || isLoadingBudgets || consultants.length === 0 }
                                onClick={ () => setBudgetRows( prev => [ ...prev, newBudgetRow() ] ) }
                            >
                                <Icon name={ 'plus' } />
                                Add consultant
                            </Button>
                        </div>
                    </div>
                </Form>
            </BasicDialogContainer>
        </BasicDialog>
    );
};

export default ConsultantHourBudgetsDialog;
